import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartPulse,
  Sprout,
  Sun,
  GraduationCap,
  Globe2,
  ArrowRight,
  ShieldCheck,
  Network,
} from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";

const PILLARS = [
  {
    icon: HeartPulse,
    title: "Health Access & System Innovation",
    text: "Technology-enabled, community-based health access focused on prevention, early detection and improved outcomes.",
  },
  {
    icon: Sprout,
    title: "Food Safety & Community Wellness",
    text: "Food safety systems, supply chain reliability and nutritional awareness tied to long-term health.",
  },
  {
    icon: Sun,
    title: "Sustainable Energy & Infrastructure",
    text: "Clean energy deployments — including solar-powered systems — supporting critical services like healthcare and education.",
  },
  {
    icon: GraduationCap,
    title: "Future-Ready Education & Workforce",
    text: "Training in technology, solar energy and emerging skills to prepare communities for modern economies.",
  },
  {
    icon: Globe2,
    title: "Global Diaspora Connectivity",
    text: "Connecting people, capital and opportunity across borders to enable cross-community collaboration.",
  },
  {
    icon: Network,
    title: "Digital Trust Infrastructure",
    text: "Real-time tracking, portable records and blockchain-backed transparency for development at scale.",
  },
];

const PROGRAMS = [
  {
    title: "Health Desert Transformation Initiative",
    text: "A comprehensive model to improve access to healthcare in underserved regions through technology, partnerships and community deployment.",
    image:
      "https://images.unsplash.com/photo-1691139600454-8509688d68f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxibGFjayUyMGhlYWx0aGNhcmUlMjBkb2N0b3IlMjBwYXRpZW50fGVufDB8fHx8MTc3OTg4ODcyMnww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Smart Community Hub",
    text: "An integrated solution combining solar energy, health access and education in a single community-based infrastructure.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/241396fa1857bef64b589bbf9b20a0eac09ec6cd9dedd9f9485275207579f472.png",
  },
  {
    title: "Future Skills Academy",
    text: "Training initiatives focused on solar technology, digital skills and workforce readiness for the next economy.",
    image:
      "https://images.pexels.com/photos/33621860/pexels-photo-33621860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroCarousel />

      {/* Who We Are */}
      <section className="section bg-[#FAF8F5]">
        <div className="container-tmb grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="overline mb-4">Who we are</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A2421] mb-6">
              Connected systems.
              <span className="text-[#C85A3A]"> Stronger communities.</span>
            </h2>
            <p className="text-base md:text-lg text-[#4A5D54] leading-relaxed mb-4">
              TMB Global Foundation is a mission-driven organization focused on expanding
              access to essential systems that improve quality of life in underserved
              communities worldwide.
            </p>
            <p className="text-base md:text-lg text-[#4A5D54] leading-relaxed mb-8">
              We operate at the intersection of healthcare, energy, food systems,
              education and digital infrastructure — designing and implementing scalable
              solutions that strengthen communities and build long-term sustainability.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="btn-primary">
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="md:col-span-7 grid grid-cols-2 gap-5">
            {[
              { v: "5", l: "Connected sectors" },
              { v: "6+", l: "Core programs" },
              { v: "Global", l: "Diaspora network" },
              { v: "100%", l: "Mission-driven" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="card-tmb p-8"
              >
                <div className="font-display text-5xl text-[#C85A3A] leading-none mb-3">
                  {s.v}
                </div>
                <div className="text-sm uppercase tracking-[0.15em] text-[#4A5D54] font-semibold">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="section bg-[#F0EBE1] relative overflow-hidden">
        <div className="container-tmb">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">Core pillars</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Six pillars. One promise — to build connected ecosystems, not isolated programs.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                  className="card-tmb p-8"
                  data-testid={`pillar-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#C85A3A]" />
                  </div>
                  <h3 className="font-display text-2xl text-[#1A2421] mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#4A5D54] leading-relaxed">{p.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section bg-[#FAF8F5]">
        <div className="container-tmb">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <div className="overline mb-4">Programs in motion</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
                How our mission lives in the world.
              </h2>
            </div>
            <Link to="/mission" data-testid="home-mission-cta" className="btn-ghost">
              See our mission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROGRAMS.map((p, i) => (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="card-tmb overflow-hidden"
                data-testid={`home-program-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-[#1A2421] mb-3 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#4A5D54] leading-relaxed">{p.text}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#2C4C3B] text-white">
        <div className="container-tmb px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { i: ShieldCheck, t: "Trust by design", d: "Blockchain-backed transparency for funds, records and impact." },
            { i: Network, t: "Cross-border by default", d: "Connecting diaspora capital and local talent to the communities that benefit." },
            { i: Globe2, t: "Built to scale", d: "Practical, scalable applications — from Detroit to Johannesburg and beyond." },
          ].map((x) => {
            const Icon = x.i;
            return (
              <div key={x.t} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#D99A29]/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#D99A29]" />
                </div>
                <div>
                  <div className="font-display text-2xl mb-2">{x.t}</div>
                  <div className="text-white/80 text-sm leading-relaxed">{x.d}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Donate banner */}
      <section className="relative overflow-hidden bg-[#2C4C3B]">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/5082e1b0ab45a266ec01f60bff1387af166c65f882639f663fee32a4e5343423.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C4C3B] via-[#2C4C3B]/90 to-[#2C4C3B]/60" />
        <div className="relative container-tmb px-6 py-24 md:py-32 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 text-white">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#D99A29] mb-4">
              Stand with us
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
              Fund the systems that change futures.
            </h2>
            <p className="text-white/80 text-lg max-w-xl">
              Your generosity powers solar-equipped clinics, food safety networks,
              workforce academies and digital trust infrastructure — built with the
              communities they serve.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/donate" data-testid="home-donate-banner-cta" className="btn-primary">
              Donate now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
